/* eslint-disable eqeqeq */
import { FEMALE_BENEFITS, OUTPATION_BENEFITS, useCotizador } from "../context/apiContext";
import { arrayIncludesNumber, equal } from "../helpers/toolsHelper";
import { shouldApplyDedusa } from "../helpers/DeductibleRules";
import { useBenefitValidation } from "./cotizador/BenefitValidationHook";

/**
 * Módulos del sidebar (1..7), única fuente de verdad para su id, texto y `labelKey`
 * del CMS. Antes vivían como un array local en `StepComponents.jsx`; ahora también los
 * consume `Step9Pages.jsx` para nombrar los módulos que devuelve `getIncompleteModules`
 * en su aviso de "faltan datos", así que quedan acá en vez de duplicados en cada lugar
 * que los necesita. `label` es solo el fallback si `texts[labelKey]` no está disponible.
 */
export const QUOTER_MODULES = [
  { id: 1, labelKey: 'menu_options_title_personal', label: 'Personal Information' },
  { id: 2, labelKey: 'menu_options_title_coverage', label: 'Coverage Options' },
  { id: 3, labelKey: 'menu_options_title_benefits', label: 'Plan Benefits' },
  { id: 4, labelKey: 'menu_options_title_benefits_ambulatorios', label: 'Beneficios ambulatorios' },
  { id: 5, labelKey: 'menu_options_title_reduccion_deducible', label: 'Reducción del Deducible' },
  { id: 6, labelKey: 'menu_options_title_riders', label: 'Riders' },
  { id: 7, labelKey: 'menu_options_title_summary', label: 'Summary' },
];

export const useSteps = () => {
  const { selectedBenefits, selectedBenefitsIds, outpationBenefitsEnabled, isCountryResidenceDeductible } =
    useCotizador();
  const {
    addDependant,
    isFemale,
    birthday,
    gender,
    name,
    surname,
    email,
    country,
    coverage,
    isEverythingLoading,
    errors,
    nonAmbulance,
    organ,
    acceptNonRegion,
    organRider,
    acceptNonRegion2,
    limitedCover,
    outpationBenefitsmaternidad,
    nonMaternidad,
    step13Confirmation,
    step14Confirmation,
    handleOnChangeBenefit
  } = useCotizador();

  const benefitValidator = useBenefitValidation(selectedBenefits);

  const getSteps = () => {
    return [...stepsGenerator()];
  };

  const getCurrentStepData = () => {
    const currentPath = window.location.pathname;
    return getSteps(addDependant).find((i) => i.path == currentPath);
  };

  const inSteps = (value) => {
    return getSteps().find((step) => step.path === value);
  };

  const getStep = (index) => {
    const step = getSteps().find((i) => i.index == index);

    if (step !== undefined && step.path == "/step13") {
      handleOnChangeBenefit({ target: { value: step13Confirmation ? 'deductbl_yes' : 'deductbl_no', name: "deductbl" } }, { text: "", id: step13Confirmation ? 'deductbl_yes' : 'deductbl_no' });
    }

    return step
  };

  const getFromAllStep = (step) => {
    return getAllSteps().find((i) => i.step === step);
  };

  const benefitsGenerator = function* (currentStep) {
    if (!currentStep) return;

    const isValidStep = (step) => {
      return (currentStep >= step) || (currentStep === -1);
    }

    if (isValidStep(4)) {
      if (!arrayIncludesNumber(Object.values(selectedBenefitsIds), "coinsurance_no")) {
        yield "maxcoinoop";
      }

      if (!Object.values(selectedBenefitsIds).includes("wwltd")) {
        if (selectedBenefitsIds["geogrcov"]) {
          yield* [ // AÑadir nueva clave
            "provdnet",
          ];
        }
      }

      if (shouldApplyDedusa(selectedBenefitsIds, isCountryResidenceDeductible)) {
        yield "dedusa";
      }

      yield* [
        "geogrcov",
        "maxcovpipoly",
        "dednonusa",
        "coinsurance"
      ];
    }
    if (isValidStep(5)) {
      yield* [
        "congenit",
        // "Dental treatment",
        // "Injuries during the training or practice of hazardous hobbies and non-professional sports",
        // "Injuries during the training or practice of non-hazardous professional sports",
        "hazardsp",
        "inmental"
      ];
      if (addDependant) {
        yield "deathcov";
      }

    }

    if (isValidStep(6)) {
      if (outpationBenefitsEnabled) {
        yield* OUTPATION_BENEFITS;
      }


    }

    if (isValidStep(7)) {
      yield* ["deductbl"];
    }

    if (isValidStep(8) && outpationBenefitsmaternidad) {
      yield* FEMALE_BENEFITS;
    }

    if (isValidStep(9) && step14Confirmation) {
      yield "healthch";
    }

    if (isValidStep(10)) {
      yield "eyesurgr";
      // yield "barsurgr";
    }
    // if (isValidStep(10)) {
    //   yield "Refractive eye surgery"
    // }
  };

  const getBenefitsPerStep = () => {
    return [...benefitsGenerator(-1)];
  };

  /**
   * ¿El step tiene todos sus campos obligatorios resueltos como para pasar al
   * siguiente? Es exactamente la condición con la que `stepsGenerator` corta la
   * cadena, pero evaluada por step de forma independiente: el generador se detiene
   * en el primero que falla y no puede decir nada de los que vienen después.
   *
   * `step` es la numeración interna (1..12), no el `module` del sidebar.
   */
  const isStepComplete = (step) => {
    switch (step) {
      // Datos del titular: todos los campos y sin errores de formato pendientes.
      case 1: {
        const normalErrors = {
          ...errors,
          dependants: false,
          organ: false,
          dependantsTable: false,
          acceptNonOrgan: false,
          acceptNonRegion: false,
          acceptNonRegion2: false,
          limitedCover: false,
          nonAmbulance: false,
          benefitsList: false,
          nonMaternidad: false
        };

        const hasFieldErrors = !!Object.values(normalErrors)
          .map((i) =>
            Object.values(i).reduce((a, b) => {
              if (a) return true;
              if (!b) return false;
              return b;
            }, false)
          )
          .filter((i) => i).length;

        if (!name || !surname || !email || !gender || !country || !coverage || !birthday) return false;

        return !hasFieldErrors;
      }

      // Tabla de dependientes (sólo aplica con addDependant).
      case 2:
      case 3:
        return !(errors['dependantsTable'] && errors['dependantsTable']['invalid']);

      case 4: {
        if (benefitValidator.shouldNotAdvance([...benefitsGenerator(4)])) return false;
        if ((selectedBenefitsIds["provdnet"] !== "glopen" && limitedCover === false) &&
          !(selectedBenefitsIds['geogrcov'] === "wwltd")) {
          return false;
        }
        if (equal(selectedBenefitsIds['geogrcov'], "wwltd") && limitedCover === false) {
          return false;
        }
        return true;
      }

      case 5: {
        if ((!organ) || benefitValidator.shouldNotAdvance([...benefitsGenerator(5)])) return false;
        if (organ && organRider == 0) return false;
        return true;
      }

      case 6: {
        if (benefitValidator.shouldNotAdvance([...benefitsGenerator(6)])) return false;
        if (!nonAmbulance && !outpationBenefitsEnabled) return false;
        return true;
      }

      case 7:
        return !(step13Confirmation && benefitValidator.shouldNotAdvance([...benefitsGenerator(7)]));

      // Maternidad: el disclaimer es obligatorio, y con él aceptado la condición
      // (maternityIsNo || newbornIsNo) && !nonMaternidad del generador nunca aplica.
      case 8: {
        if (!nonMaternidad) return false;
        return !benefitValidator.shouldNotAdvance([...benefitsGenerator(8)]);
      }

      case 9:
        return !(step14Confirmation && benefitValidator.shouldNotAdvance([...benefitsGenerator(9)]));

      case 10:
        return !benefitValidator.shouldNotAdvance([...benefitsGenerator(10)]);

      // Summary (/step8): checkboxes de aceptación.
      case 11:
        return !!acceptNonRegion && !!acceptNonRegion2;

      // /step9 (envío de PDF/email) es el final del flujo: no tiene nada que validar.
      default:
        return true;
    }
  };

  const stepsGenerator = function* () {
    let index = 0;

    yield { path: "/step1", module: 1, index: index++, step: 1 };
    // yield { path: "/step12", module: 1, index: index++, step: 12 };

    if (!isStepComplete(1)) return;

    if (addDependant) {
      yield { path: "/step2", module: 1, index: index++, step: 2 };
      yield { path: "/step3", module: 1, index: -10, step: 3 };

      if (!isStepComplete(2)) return;
    }

    if (isEverythingLoading()) return;

    yield { path: "/step11", module: 2, index: index++, step: 4 };
    if (!isStepComplete(4)) return;

    yield { path: "/step4", module: 3, index: index++, step: 5 };
    if (!isStepComplete(5)) return;

    yield { path: "/step5", module: 7, index: index++, step: 6 };
    if (!isStepComplete(6)) return;

    yield { path: "/step13", module: 7, index: index++, step: 7 };
    if (!isStepComplete(7)) return;

    if (isFemale()) {
      yield { path: "/step6", module: 3, index: index++, step: 8 };
      if (!isStepComplete(8)) return;
    }

    //yield { path: "/step7", module: 4, index: index++, step: 8 };
    yield { path: "/step14", module: 5, index: index++, step: 9 };
    if (!isStepComplete(9)) return;

    yield { path: "/step15", module: 3, index: index++, step: 10 };
    if (!isStepComplete(10)) return;

    yield { path: "/step8", module: 5, index: index++, step: 11 };
    if (!isStepComplete(11)) return;

    yield { path: "/step9", module: 5, index: index++, step: 12 };
  };


  const getAllSteps = () => {
    return [
      { path: "/step1", module: 1, index: 0, step: 1 },
      { path: "/step2", module: 1, index: 1, step: 2 },
      { path: "/step3", module: 1, index: 2, step: 3 },
      { path: "/step11", module: 2, index: 3, step: 4 },
      { path: "/step4", module: 3, index: 3, step: 5 },
      { path: "/step5", module: 4, index: 4, step: 6 },
      { path: "/step13", module: 5, index: 5, step: 7 },
      { path: "/step6", module: 6, index: 6, step: 8, ignore: !isFemale() },
      { path: "/step14", module: 6, index: 7, step: 9 },
      { path: "/step15", module: 6, index: 8, step: 10 },
      //{ path: "/step7", module: 4, index: 6, step: 8 },
      { path: "/step8", module: 7, index: 9, step: 11 },

      { path: "/step9", module: 7, index: 10, step: 12 },
      // { path: "/step12", module: 6, index: 9, step: 11 },
    ];
  };

  // Steps que realmente forman parte del flujo actual: los dependientes sólo
  // existen con addDependant y /step6 (maternidad) sólo cuando isFemale().
  const getApplicableSteps = () => {
    return getAllSteps().filter((i) => {
      if (i.ignore) return false;
      if (i.step === 2 || i.step === 3) return !!addDependant;
      return true;
    });
  };

  /**
   * Módulos del sidebar (1..7) con campos pendientes. A diferencia de `getSteps`,
   * evalúa cada step por separado, así que no se detiene en el primero incompleto.
   * Devuelve [] mientras los catálogos de beneficios están cargando: ahí todavía
   * no hay nada seleccionado y todos los steps darían falso negativo.
   */
  const getIncompleteModules = () => {
    if (isEverythingLoading()) return [];

    const incompleteModules = getApplicableSteps()
      .filter((i) => !isStepComplete(i.step))
      .map((i) => i.module);

    return [...new Set(incompleteModules)];
  };

  return {
    getAllSteps,
    getCurrentStepData,
    getSteps,
    getStep,
    inSteps,
    getBenefitsPerStep,
    benefitsGenerator,
    getFromAllStep,
    isStepComplete,
    getIncompleteModules
  };
};
